import { useMemo } from "react";
import {
    getEntityLockedTooltip,
    isEntityLockedTaskType,
    type Task,
    type TaskStatus,
} from "@common/types/task";
import { TaskColumn } from "@common/types/views/tasksView";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import type {
    DataGridDraftRowOptions,
    DataGridListEditingOptions,
} from "@design-system/DataGrid";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { useScope } from "@state/scopeContext.lib";
import type { NestedTaskRow } from "../types";
import { hasTaskFields } from "./cells/taskRowCells";
import { useTaskListCreate } from "./useTaskListCreate";
import { useTaskListStatusChange } from "./useTaskListStatusChange";
import { type TaskListUpdate, useTaskListUpdate } from "./useTaskListUpdate";

/** What the list's editors and draft rows save through; stable for the table's life. */
export interface TaskListActions {
    update: TaskListUpdate;
    /** Asks first when the status would finish a task with open blockers. */
    changeStatus: (change: { task: Task; status: TaskStatus }) => Promise<void>;
    /** A draft row's title: creates the task with the draft's fields. */
    create: (draft: { row: NestedTaskRow; value: string }) => void;
    cancelDraft: (row: NestedTaskRow) => void;
}

/**
 * The task list's stable actions, the status confirmation to spread onto
 * `TaskStatusTerminationConfirmationModal`, and whether the page has
 * services and stages to pick (an organization's page).
 */
export const useTaskListActions = () => {
    const update = useTaskListUpdate();
    const { changeStatus, confirmation } = useTaskListStatusChange(update);
    const { create, cancelDraft } = useTaskListCreate();
    const { organizationId } = useScope();
    const latestRef = useLatestRef({ update, changeStatus, create });
    const actions = useMemo<TaskListActions>(
        () => ({
            update: (request) => latestRef.current.update(request),
            changeStatus: (change) => latestRef.current.changeStatus(change),
            create: (draft) => latestRef.current.create(draft),
            cancelDraft,
        }),
        [latestRef, cancelDraft],
    );
    return { actions, confirmation, hasCloseOptions: Boolean(organizationId) };
};

/** Draft rows (`useGroupedTasks`' placeholders): a "Create task" input in the Task column. */
export const getTaskListDraftRows = (
    actions: TaskListActions,
): DataGridDraftRowOptions<NestedTaskRow> => ({
    isDraft: (row) => Boolean(row.isNewPlaceholder),
    columnId: TaskColumn.NAME,
    placeholder: "Create task",
    onCreate: actions.create,
    onCancel: actions.cancelDraft,
    shouldAutoFocus: (row) => Boolean(row.shouldAutoFocus),
});

const isEntityLockedCell = ({
    row,
    columnId,
}: {
    row: NestedTaskRow;
    columnId: string;
}) =>
    columnId === TaskColumn.ENTITIES &&
    hasTaskFields(row) &&
    isEntityLockedTaskType(row.type);

/**
 * Which task cells the list edits (group and draft rows never reach it):
 * none on busy closes demo rows; on an organization's page, Service when the
 * row's client has services and Stage when the organization has stages;
 * Entities unless the task type keeps its own. Rebuild when an input
 * changes, so rows redraw with the answer.
 */
export const getTaskListEditing = ({
    hasCloseOptions,
    serviceWorkspaceIds,
    hasCloseStages,
}: {
    /** The page belongs to an organization. */
    hasCloseOptions: boolean;
    /** The clients that have at least one service. */
    serviceWorkspaceIds: ReadonlySet<string>;
    hasCloseStages: boolean;
}): DataGridListEditingOptions<NestedTaskRow> => ({
    isCellEditable: ({ row, columnId }) => {
        if (isBusyClosesDemoId(row.id)) {
            return false;
        }
        if (columnId === TaskColumn.CLOSE) {
            return (
                hasCloseOptions &&
                row.workspaceId !== undefined &&
                serviceWorkspaceIds.has(row.workspaceId)
            );
        }
        if (columnId === TaskColumn.CLOSE_STAGE) {
            return hasCloseOptions && hasCloseStages;
        }
        return !isEntityLockedCell({ row, columnId });
    },
    getReadOnlyReason: (cell) =>
        isEntityLockedCell(cell) && hasTaskFields(cell.row)
            ? getEntityLockedTooltip(cell.row.type)
            : undefined,
});
